import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { User, Lock, Mail, ArrowRight, UserPlus, LogIn, Eye, EyeOff } from 'lucide-react';
import logo from '../../assets/logo.png';
import './Auth.css';

const REGISTRATION_DISABLED = true;

const AuthPage = ({ onAuthSuccess, onBack }) => {
  const { login, register } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      let result;
      if (isLoginMode) {
        result = login(formData.username, formData.password);
      } else {
        result = register(formData.username, formData.password, formData.displayName);
      }

      if (result.success) {
        if (onAuthSuccess) {
          onAuthSuccess(result.user);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    if (REGISTRATION_DISABLED && isLoginMode) return;
    setIsLoginMode(!isLoginMode);
    setError('');
    setFormData({ username: '', password: '', displayName: '' });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-form-section">
          <div className="auth-header">
            <div className="auth-logo">
              <img src={logo} alt="RMW Logo" className="auth-logo-img" />
            </div>
            <h1>{isLoginMode ? 'Welcome Back' : 'Create Account'}</h1>
            <p>
              {isLoginMode 
                ? 'Sign in to access your research designs' 
                : 'Start your research methodology journey'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {!isLoginMode && (
              <div className="form-group">
                <label htmlFor="displayName">
                  <User size={16} />
                  Display Name
                </label>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  placeholder="How should we call you?"
                  autoComplete="name"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="username">
                <Mail size={16} />
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Enter your username"
                required
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                <Lock size={16} />
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  required
                  autoComplete={isLoginMode ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                'Please wait...'
              ) : isLoginMode ? (
                <>
                  <LogIn size={18} />
                  Sign In
                </>
              ) : (
                <>
                  <UserPlus size={18} />
                  Create Account
                </>
              )}
            </button>
          </form>

          {REGISTRATION_DISABLED && isLoginMode ? (
            <div className="auth-registration-closed">
              New registrations are currently closed. Please check back soon.
            </div>
          ) : (
            <div className="auth-switch">
              <span>
                {isLoginMode ? "Don't have an account?" : 'Already have an account?'}
              </span>
              <button type="button" onClick={toggleMode}>
                {isLoginMode ? 'Sign Up' : 'Sign In'}
                <ArrowRight size={16} />
              </button>
            </div>
          )}

          {onBack && (
            <button className="auth-back-btn" onClick={onBack}>
              Continue as Guest
            </button>
          )}
        </div>

        <div className="auth-hero-section">
          <div className="auth-hero-content">
            <h2>Research Methodology Wizard</h2>
            <p>From Research Gaps, to Research Questions, Framework and Design</p>
            
            <div className="auth-hero-features">
              <div className="auth-hero-feature" style={{background: '#0d9488'}}>
                <span className="feature-title">Wizard A: Research Gap Finder</span>
              </div>
              <div className="auth-hero-feature" style={{background: '#4f46e5'}}>
                <span className="feature-title">Wizard B: Research Methodology</span>
              </div>
              <div className="auth-hero-feature" style={{background: '#0891b2'}}>
                <span className="feature-title">Wizard C: Data Analysis Planning</span>
              </div>
              <div className="auth-hero-feature" style={{background: '#7c3aed'}}>
                <span className="feature-title">Wizard D: Framework Builder</span>
              </div>
              <div className="auth-hero-feature" style={{background: '#059669'}}>
                <span className="feature-title">Wizard E: Data Collection Process</span>
              </div>
              <div className="auth-hero-feature" style={{background: '#dc2626'}}>
                <span className="feature-title">Wizard F: Ethics Approval</span>
              </div>
              <div className="auth-hero-feature" style={{background: '#0ea5e9'}}>
                <span className="feature-title">Wizard G: Survey Builder & Analysis</span>
              </div>
            </div>

            <div className="auth-hero-tagline">
              <p>Create an account to save your progress and access premium features</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
